import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { getPlayers } from '@/lib/api'
import { PageMotion } from '@/components/Motion'
import { ErrorBlock, LoadingBlock, PageHeader, Panel } from '@/components/ui'
import { cn, formatKD, formatNum, formatPct } from '@/lib/utils'
import { Search } from 'lucide-react'

const SORTS = [
  { id: 'kills', label: 'Kills' },
  { id: 'kd', label: 'K/D' },
  { id: 'accuracy', label: 'Accuracy' },
  { id: 'missions', label: 'Missions' },
  { id: 'name', label: 'Name' },
]

export default function Players() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState('kills')

  const { data, isLoading, error } = useQuery({
    queryKey: ['players', page, q, sort],
    queryFn: () => getPlayers(page, 25, q, sort),
  })

  return (
    <PageMotion className="space-y-6">
      <PageHeader
        title="Players"
        subtitle={data ? `${formatNum(data.total)} in the leaderboard` : 'Career leaderboard'}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <form
          className="flex gap-2 flex-1"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(search.trim())
          }}
        >
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              className="input pl-9"
              placeholder="Search players…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
        <div className="flex flex-wrap gap-1.5 p-1 rounded-panel border border-line bg-canvas-raised/60">
          {SORTS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={cn(
                'rounded-control px-2.5 py-1.5 text-xs font-medium transition-colors',
                sort === s.id
                  ? 'bg-accent text-canvas'
                  : 'text-ink-muted hover:text-ink',
              )}
              onClick={() => {
                setSort(s.id)
                setPage(1)
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <Panel flush>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <div className="p-4">
            <ErrorBlock message="Failed to load players" />
          </div>
        ) : (
          <>
            <div className="table-wrap border-0 rounded-none">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Missions</th>
                    <th>Kills</th>
                    <th>Deaths</th>
                    <th>K/D</th>
                    <th>Acc</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.data ?? []).map((p, i) => (
                    <tr key={p.id}>
                      <td className="font-mono text-xs text-ink-faint">
                        {(page - 1) * 25 + i + 1}
                      </td>
                      <td>
                        <Link to={`/players/${p.id}`} className="link-accent font-medium">
                          {p.name}
                        </Link>
                      </td>
                      <td className="font-mono">{formatNum(p.missionsPlayed)}</td>
                      <td className="font-mono text-accent-text">{formatNum(p.kills)}</td>
                      <td className="font-mono">{formatNum(p.deaths)}</td>
                      <td className="font-mono">{formatKD(p.kd)}</td>
                      <td className="font-mono text-xs">{formatPct(p.accuracy)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data && data.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-line text-sm">
                <span className="text-ink-faint">
                  Page {data.page} / {data.totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </PageMotion>
  )
}
